import { useEffect, useState } from "react";
import { api } from "@src/utils/axios-instance";
import SearchInput from "@src/components/SearchInput/SearchInput";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Chip from "@src/components/Chip/Chip";
import Loader from "@src/components/Loader/Loader";
import { User } from "@src/types/global";
type Item = {
    id: string;
    name: string;
};
interface IProps {
    characterId: string;
}
/**
 * Allows to search for a user by username. On click, grants the user access to the character
 * Granting access to a character, even public, allows a user to add this character to worlds as well as create links between their characters
 * @param {string} characterId - The id of the character to grant access to
 */
const UserAccessGrant = ({ characterId }: IProps) => {
    const [displayResults, setDisplayResults] = useState<(Item | null)[]>([]);
    const [_, setSearchResults] = useState<User[]>([]);

    const queryClient = useQueryClient();
    const prevData = queryClient.getQueryData([
        "characters",
        characterId,
        "user_grants",
    ]) as Item[];
    const [selectedItems, setSelectedItems] = useState<Item[]>(prevData);
    /**
     * The function to call whenever a user types in the box
     * @param {string} - value - The user input
     */
    const handleChange = async (value: string) => {
        setSearchValue(value);
    };

    async function parseData() {
        if (searchValue !== "") {
            await userQuery.refetch().then((res) => {
                if (res.data && res.data.length > 0) {
                    transformSearchResults(res.data!);
                    setSearchResults(res.data!);
                } else {
                    setDisplayResults([]);
                    setSearchResults([]);
                }
            });
        } else {
            setDisplayResults([]);
            setSearchResults([]);
        }
    }
    /**
     * Transforms the array of given user to an array of {name: string, id: string} objects
     * for display in the searchResults
     * @param {User[]} - users - The array of users coming from the server to convert
     */
    function transformSearchResults(users: User[]) {
        if (users && users.length) {
            const usersArray = users.map((user) => {
                if (selectedItems.find((i) => i.id == user.id) == undefined) {
                    return {
                        name: user.username,
                        id: user.id,
                    };
                } else {
                    return null;
                }
            });
            //Remove already selected users
            setDisplayResults(usersArray);
        }
    }
    const [searchValue, setSearchValue] = useState("");
    const userQuery = useQuery({
        queryKey: ["username", searchValue],
        queryFn: () => fetchByUserName(searchValue),
        enabled: false,
    });

    const REVOKE = "revoke";
    const GRANT = "grant";
    async function changeAccess(data: {
        user: Item;
        action: "revoke" | "grant";
    }) {
        api.patch(`/characters/${characterId}/grant`, {
            character_access_grant: {
                user_id: data.user.id,
                granted: data.action == GRANT,
                revoked: data.action == REVOKE,
            },
        });
    }
    async function getCharacterGrants() {
        const res = await api.get(`/characters/${characterId}/grant`);
        const items = res.data.data.map(
            (grant: { user: string; user_id: string }) => ({
                name: grant.user,
                id: grant.user_id,
            }),
        );
        setSelectedItems(items);
        return items;
    }
    const accessMutation = useMutation({
        mutationFn: changeAccess,
        mutationKey: ["characters", characterId, "user_grants"],
        onSuccess: (_, variables) => {
            if (variables.action == REVOKE) {
                const newItems = selectedItems.filter(
                    (i) => i.id !== variables.user.id,
                );
                setSelectedItems(newItems);
                queryClient.setQueryData(
                    ["characters", characterId, "user_grants"],
                    newItems,
                );
            }
        },
    });
    const getGrants = useQuery({
        queryFn: getCharacterGrants,
        queryKey: ["characters", characterId, "user_grants"],
        staleTime: 6000,
        placeholderData: (previousData, _) => previousData,
    });
    function pickUser(item: Item) {
        if (selectedItems.find((i) => item.id == i.id) == undefined) {
            setSelectedItems((oldState) => {
                return [...oldState, item];
            });
        }
        accessMutation.mutate({ user: item, action: GRANT });
    }

    useEffect(() => {
        parseData();
    }, [searchValue]);
    async function fetchByUserName(name: string): Promise<User[]> {
        const { data } = await api.get("users/?username=" + name);
        return data.data;
    }
    if (getGrants.isPending) {
        return (
            <div className="form__group">
                <p>Loading access grants...</p>
                <Loader />
            </div>
        );
    }
    return (
        <div className="form__group">
            <h4>Access grants :</h4>
            <SearchInput
                searchResults={displayResults}
                onChange={handleChange}
                isLoading={userQuery.isFetching}
                placeholder="Enter a username"
                name="grant-access"
                onClick={pickUser}
            />
            <div>
                {selectedItems.map((item) => (
                    <Chip
                        key={item.id}
                        displayText={item.name}
                        id={item.id}
                        onDelete={() => {
                            accessMutation.mutate({
                                user: item,
                                action: REVOKE,
                            });
                        }}
                    />
                ))}
            </div>

            <p className="info">
                Granting access to a character allows a user to add this
                character to worlds as well as create links between their
                characters.
            </p>
        </div>
    );
};
export default UserAccessGrant;
